import { posthog } from "@/lib/posthog";
import { ANALYTICS_EVENTS, type AnalyticsEventName, type AnalyticsProperties } from "./events";

/**
 * Central analytics service. Every product surface should call `track()`
 * instead of `posthog.capture()` directly so event names stay typed
 * (see events.ts) and so fire-once dedupe is applied consistently.
 */

const firedOnce = new Set<string>();

/** Fires an event exactly once per page load for a given key (e.g. "hero_viewed"). */
export function trackOnce(key: string, name: AnalyticsEventName, properties?: AnalyticsProperties) {
  if (firedOnce.has(key)) return;
  firedOnce.add(key);
  track(name, properties);
}

export function track(name: AnalyticsEventName, properties?: AnalyticsProperties) {
  try {
    posthog?.capture(name, properties);
  } catch {
    // Analytics must never break the product surface it's instrumenting.
  }
}

const ATTRIBUTION_STORAGE_KEY = "novari_attribution_v1";
const VISITED_BEFORE_KEY = "novari_visited_before";

interface AttributionProperties {
  first_referrer: string;
  first_landing_page: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  is_returning_visitor: boolean;
}

/**
 * Captures UTM params + referrer on the visitor's first touch, persists them
 * for the lifetime of the browser (localStorage — outlives the session), and
 * registers them as PostHog super properties so every subsequent event
 * (including post-signup ones) carries the original acquisition context.
 * Safe to call on every page load; only writes on first touch.
 */
let attributionInitialized = false;

export function initVisitorAttribution() {
  if (typeof window === "undefined" || attributionInitialized) return;
  attributionInitialized = true;

  // Attribution is optional: denied storage or corrupt old JSON must not
  // throw from GlobalAnalytics and take down the application.
  try {
  const isReturning = localStorage.getItem(VISITED_BEFORE_KEY) === "true";
  localStorage.setItem(VISITED_BEFORE_KEY, "true");

  const existing = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
  let attribution: AttributionProperties;

  if (existing) {
    attribution = { ...JSON.parse(existing), is_returning_visitor: isReturning };
  } else {
    const params = new URLSearchParams(window.location.search);
    attribution = {
      first_referrer: document.referrer || "direct",
      first_landing_page: window.location.pathname,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content"),
      is_returning_visitor: false,
    };
    localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  }

  try {
    // Super properties: attached to every event (including post-signup ones)
    // for the lifetime of the browser, so attribution survives identify().
    posthog?.register({
      first_referrer: attribution.first_referrer,
      first_landing_page: attribution.first_landing_page,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      is_returning_visitor: isReturning,
    });
    // Person properties: `set` keeps is_returning_visitor current, `setOnce`
    // preserves the *original* acquisition touch even if it's overwritten
    // by super properties on a later visit with different/no UTMs.
    posthog?.setPersonProperties(
      { is_returning_visitor: isReturning },
      {
        initial_referrer: attribution.first_referrer,
        initial_landing_page: attribution.first_landing_page,
        initial_utm_source: attribution.utm_source,
        initial_utm_medium: attribution.utm_medium,
        initial_utm_campaign: attribution.utm_campaign,
      }
    );
  } catch {
    // non-fatal
  }
  } catch { /* Storage is unavailable or old attribution is malformed. */ }
}

/** Re-exported for call sites that only need the constants. */
export { ANALYTICS_EVENTS };
